import { useId, useState, type FormEvent } from 'react';

import { ApiError } from '@mds/shared/lib/api';
import { stamp } from '@mds/shared/lib/dates';
import type { AdminFile, FileMimeType, FileVisibility } from '@mds/shared/types/file';
import Select from '@mds/shared/ui/select';
import SegmentedToggle from '@mds/shared/ui/segmented-toggle';

import FileThumb from '@/components/files/file-thumb';
import Button from '@/components/ui/button';
import Chip from '@/components/ui/chip';
import Field, { inputCls, selectCls } from '@/components/ui/field';
import Modal from '@/components/ui/modal';
import * as files from '@/files/files.api';
import {
  destinationOptions,
  pathLabel,
  toFolderId,
  toFolderValue,
  type FolderTree,
} from '@/files/folders';
import { statusTone } from '@/files/status-tone';
import { formatBytes } from '@/files/upload';
import { fileTypeLabel } from '@/lib/labels';

/** Extensions a name may already end in for its type; the first is the one the API adds. */
const EXTENSIONS: Record<FileMimeType, readonly string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
};

/**
 * The editable part of a name and the extension that stays. The API puts the
 * right extension back on any name that drops it, so the field only ever edits
 * what comes before it.
 */
function splitName(name: string, mimeType: FileMimeType) {
  const lower = name.toLowerCase();
  const kept = EXTENSIONS[mimeType].find((extension) => lower.endsWith(extension));
  return kept
    ? { stem: name.slice(0, -kept.length), extension: name.slice(-kept.length) }
    : { stem: name, extension: EXTENSIONS[mimeType][0] };
}

const VISIBILITY_OPTIONS = [
  { value: 'private', label: 'Private' },
  { value: 'public', label: 'Public' },
] as const;

/**
 * One file: its preview, where it lives, and the controls to rename it, move
 * it, switch who can open it, download, delete, restore or purge it.
 *
 * Mount it with `key={file.id}` so the form starts from the file it opened on.
 * A refreshed copy of the same file updates what is shown without wiping what
 * is being typed.
 */
export default function FileDialog({
  file,
  thumbnailUrl,
  tree,
  mayManage,
  mayPurge,
  busy,
  actionError,
  onClose,
  onSaved,
  onDownload,
  onDelete,
  onRestore,
  onPurge,
}: {
  file: AdminFile;
  thumbnailUrl: string | null;
  tree: FolderTree;
  mayManage: boolean;
  mayPurge: boolean;
  /** Another action on this file is in flight. */
  busy: boolean;
  /** Why the last download, delete, restore or purge from here failed. */
  actionError: string | null;
  onClose: () => void;
  onSaved: (file: AdminFile) => void | Promise<void>;
  onDownload: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onPurge: () => void;
}) {
  const initial = splitName(file.originalFilename, file.mimeType);
  const [stem, setStem] = useState(initial.stem);
  const [folder, setFolder] = useState(toFolderValue(file.folderId));
  const [visibility, setVisibility] = useState<FileVisibility>(file.visibility);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameId = useId();

  const editable = mayManage && !file.deletedAt;
  const name = `${stem.trim()}${initial.extension}`;
  const changes: files.FileChanges = {
    ...(name !== file.originalFilename ? { originalFilename: name } : {}),
    ...(toFolderId(folder) !== file.folderId ? { folderId: toFolderId(folder) } : {}),
    ...(visibility !== file.visibility ? { visibility } : {}),
  };
  const dirty = Object.keys(changes).length > 0;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!dirty) return;
    if (!stem.trim()) return setError('Give the file a name.');
    if (name.length > 255) return setError('Keep the name to 255 characters or fewer.');
    setSaving(true);
    setError(null);
    try {
      await onSaved(await files.updateFile(file.id, changes));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The changes could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const ready = file.uploadStatus === 'ready' && !file.deletedAt;

  return (
    <Modal
      open
      onClose={onClose}
      title={file.originalFilename}
      footer={
        <>
          {editable && (
            <Button disabled={!dirty || saving || busy} onClick={(e) => void save(e)}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          )}
          {ready && (
            <Button variant={editable ? 'quiet' : 'dark'} disabled={busy} onClick={onDownload}>
              Download
            </Button>
          )}
          {mayManage && !file.deletedAt && (
            <Button variant="danger" disabled={busy || saving} onClick={onDelete}>
              Delete
            </Button>
          )}
          {mayManage && file.deletedAt && (
            <Button disabled={busy} onClick={onRestore}>
              Restore
            </Button>
          )}
          {mayPurge && file.deletedAt && (
            <Button variant="danger" disabled={busy} onClick={onPurge}>
              Purge permanently
            </Button>
          )}
          <Button variant="quiet" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {file.uploadStatus === 'ready' && (
          <FileThumb url={thumbnailUrl} mimeType={file.mimeType} size="large" />
        )}

        <div className="flex flex-wrap gap-2">
          <Chip className={'border ' + statusTone(file)}>
            {file.deletedAt ? 'deleted' : file.uploadStatus}
          </Chip>
          {!editable && <Chip>{file.visibility}</Chip>}
          <Chip>{fileTypeLabel(file.mimeType)}</Chip>
          <Chip>{formatBytes(file.sizeBytes)}</Chip>
        </div>

        {actionError && (
          <p
            role="alert"
            className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
          >
            {actionError}
          </p>
        )}

        {file.rejectionReason && (
          <p
            role="alert"
            className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
          >
            {file.rejectionReason}
          </p>
        )}

        {editable && (
          <form className="flex flex-col gap-5" onSubmit={(e) => void save(e)}>
            {error && (
              <p
                role="alert"
                className="m-0 rounded-xl border border-danger-edge bg-danger-wash px-4 py-3 text-base text-danger-text"
              >
                {error}
              </p>
            )}

            {/* Not a Field: that labels its one child, and this row is the input plus its extension. */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor={nameId}
                className="font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase"
              >
                Name
              </label>
              <span className="flex items-stretch gap-2">
                <input
                  id={nameId}
                  value={stem}
                  maxLength={255 - initial.extension.length}
                  onChange={(e) => {
                    setStem(e.target.value);
                    setError(null);
                  }}
                  aria-describedby={`${nameId}-extension`}
                  className={inputCls + ' min-w-0 flex-1'}
                />
                <span
                  id={`${nameId}-extension`}
                  title="The extension stays, so the file still opens once downloaded"
                  className="flex items-center rounded-xl border border-hairline bg-raised px-3 font-mono text-base text-on-raised"
                >
                  {initial.extension}
                </span>
              </span>
            </div>

            <Field label="Folder">
              <Select
                variant="field"
                className={selectCls}
                value={folder}
                onChange={setFolder}
                options={destinationOptions(tree)}
              />
            </Field>

            <div className="flex flex-col gap-2">
              <span
                aria-hidden="true"
                className="font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase"
              >
                Visibility
              </span>
              <SegmentedToggle
                value={visibility}
                options={VISIBILITY_OPTIONS}
                onChange={setVisibility}
                aria-label="Visibility"
                className="self-start"
              />
              <span className="text-base text-ink-muted">
                {visibility === file.visibility ? (
                  visibility === 'public' ? (
                    'Anyone with the link can open it, and images can go in event and space galleries.'
                  ) : (
                    'Only staff signed in here can open it.'
                  )
                ) : visibility === 'private' ? (
                  <span className="text-warn-text">
                    Once saved, any public page showing it — an event, a space, the café page —
                    stops showing it.
                  </span>
                ) : (
                  'Once saved, anyone with the link can open it.'
                )}
              </span>
            </div>
          </form>
        )}

        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
          {!editable && (
            <>
              <dt className="font-mono text-sm text-ink-muted uppercase">Folder</dt>
              <dd className="m-0">{pathLabel(tree, file.folderId)}</dd>
            </>
          )}

          <dt className="font-mono text-sm text-ink-muted uppercase">Added</dt>
          <dd className="m-0 font-mono">{stamp(file.createdAt)}</dd>

          <dt className="font-mono text-sm text-ink-muted uppercase">Verified</dt>
          <dd className="m-0 font-mono">{stamp(file.verifiedAt)}</dd>

          {file.uploadStatus === 'pending' && (
            <>
              <dt className="font-mono text-sm text-ink-muted uppercase">Upload expires</dt>
              <dd className="m-0 font-mono">{stamp(file.uploadExpiresAt)}</dd>
            </>
          )}

          {file.deletedAt && (
            <>
              <dt className="font-mono text-sm text-ink-muted uppercase">Deleted</dt>
              <dd className="m-0 font-mono">{stamp(file.deletedAt)}</dd>
            </>
          )}

          <dt className="font-mono text-sm text-ink-muted uppercase">Checksum</dt>
          <dd className="m-0 font-mono text-sm break-all">{file.checksumSha256}</dd>

          {file.contentUrl && (
            <>
              <dt className="font-mono text-sm text-ink-muted uppercase">Public URL</dt>
              <dd className="m-0 font-mono text-sm break-all">{file.contentUrl}</dd>
            </>
          )}
        </dl>
      </div>
    </Modal>
  );
}
